import {
  DropdownMenu, Icon, IconName, Spinner,
} from '@usealloy/component-library';

import {
  BetaPreferencesProvider,
  useBetaController,
  useBetaToggle,
} from '~/dashboard/BetaPreferencesProvider';
import type { BetaKey } from '~/dashboard/BetaPreferencesProvider';

// Menu-side labels (surface-owned; shorter than Settings' copy). Array order = display order.
const MENU_ITEMS: { key: BetaKey; label: string }[] = [
  { key: 'betaRoutes', label: 'Applications' },
  { key: 'fepSidenav', label: 'Side Navigation' },
];

/** A single beta toggle row in the agent menu, wired to the shared controller. */
const BetaFeatureCheckboxItem = ({ betaKey, label }: { betaKey: BetaKey; label: string }) => {
  const {
    available, checked, disabled, loading, setEnabled,
  } = useBetaToggle(betaKey);

  if (!available) {
    return null;
  }

  return (
    <DropdownMenu.Checkbox
      checked={checked}
      disabled={disabled}
      onCheckedChange={value => { setEnabled(value); }}
      // Keep the submenu open on select so a toggle doesn't dismiss the menu before the
      // write resolves (the controller handles nav/reload on success).
      onSelect={e => e.preventDefault()}
    >
      <span style={{ flex: 1 }}>{label}</span>
      {loading && <Spinner size="small" />}
    </DropdownMenu.Checkbox>
  );
};

/** Renders the "Beta Features" submenu, or nothing when the org has no beta options available. */
const BetaFeaturesSubmenu = () => {
  const { isAvailable } = useBetaController();

  if (!isAvailable) {
    return null;
  }

  return (
    <>
      <DropdownMenu.Sub>
        <DropdownMenu.SubTrigger>
          <Icon icon={IconName.Flask} size="small" />
          <span>Beta Features</span>
        </DropdownMenu.SubTrigger>
        <DropdownMenu.SubContent>
          {MENU_ITEMS.map(({ key, label }) => (
            <BetaFeatureCheckboxItem key={key} betaKey={key} label={label} />
          ))}
        </DropdownMenu.SubContent>
      </DropdownMenu.Sub>
      <DropdownMenu.Separator />
    </>
  );
};

/**
 * Agent-menu "Beta Features" entry, wired to the shared beta-preferences controller
 * (source="agentMenu"). Lives inside the profile DropdownMenu content, so the provider —
 * and its refetch-on-focus listener — only mounts while the menu is open.
 */
export const BetaFeaturesMenu = () => (
  <BetaPreferencesProvider source="agentMenu">
    <BetaFeaturesSubmenu />
  </BetaPreferencesProvider>
);
